import { FilterEntry } from '@/entities/filter/model';
import ConductedByWithDeletedFilter from '@/ui/components/filters/ConductedByWithDeletedFilter.vue';
import { h, defineComponent, ref, watch, computed } from 'vue';
import { box, column, indexView, loader, tag, textColumn } from '@/ui/components';
import { filterFormLoader } from '@/ui/components/Loader';
import {
  lastUpdatedFilter,
  questionTitleFilter,
  responseFilter,
  searchFilter,
  entityItemFilter,
  approvalStatusFilter,
} from '@/ui/components/filters/filters-horizontal';
import {
  bulkDelete,
  list as getInspectionList,
  getPagination as getInspectionPagination,
  remove,
} from '@/services/api/resources/inspection';
import { InspectionList, InspectionRecord } from '@/entities/inspection';
import { d, t } from '@/ui/i18n/i18n';
import { filterListToOptions, sequence, toFixed } from '@/ui/helpers';
import { SelectOptions, SortDirection } from '@/ui/types';
import * as qs from '@/ui/queryState';
import { eventBus } from '@/_legacy/eventBus';
import { Events } from '@/ui/events';
import { usePagination, useSorting, useTask } from '@/ui/concerns';
import { filter as getAllUsers } from '@/services/api/resources/user';
import { filter as getAllEntityItems } from '@/services/api/resources/entityItem';
import { filterStatus as getAllApprovalStatuses } from '@/services/api/resources/approval';
import { emptyList } from '@/services/api';
import { Status } from '@/entities/todo';
import { RouteName } from '@/ui/routes';
import { useInspectionCheck } from '@/ui/pages/inspection/useInspectionCheck';
import InspectionDoneEmpty from '@/ui/pages/inspection/InspectionDoneEmpty.vue';
import { Tooltip } from '@/ui/components/Tooltip';
import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
import { bulkDeleteButton } from '@/ui/components/Button';
import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';
import { featureExplanationIcon } from '@/ui/components/icon/icons';
import { inspectionIndexExport } from '@/ui/pages/inspection/InspectionIndexExport';
import { SupportedExportFormatEnum } from '@/services/api/resources/inspection/exports';
import { manageIssuesButton } from '@/ui/components/issue/ManageIssuesModal/ManageIssuesButton';
import { manageIssueModal } from '@/ui/components/issue/ManageIssuesModal';
import { IssueType } from '@/entities/issue';
import { IssueRecord } from '@/entities/issue/v2';
import { ApprovalStatus } from '@/entities/approvals/constants';
import { SortingIcon } from '@/ui/components/icon/SortingIcon';
import { approvalsEnabledForOrganization, user } from '@/ui/auth/auth';
import { InspectionQuery } from '@/services/api/resources/inspection/query';
import { useSubmittedForm } from '@/ui/concerns/useSubmittedForm';
import { approvalStatusToText } from '@/ui/dictionary';
import SystemRejectedButton from '@/ui/components/inspection/SystemRejectedButton.vue';
import { approvalStatusTagClass } from '@/ui/helpers/approvalsHelper';
import { Pagination } from '@/ui/components/Pagination';

export const InspectionDoneTab = defineComponent({
  name: 'InspectionDoneTab',
  setup() {
    // state stored in query string
    const { page, perPage, resetPagination } = usePagination();
    const { sort, dir, onSortChange } = useSorting('doneAt', SortDirection.Descending);
    const { checkResult, performCheck } = useInspectionCheck([Status.Done, Status.SystemRejected]);
    const total = ref(0);
    const totalLoading = ref(true);
    const isPaginationFetchRequired = ref(true);

    const search = qs.string('q', '');
    const assignee = qs.numbers('assignee', []);
    const doneBy = qs.numbers('doneBy', []);
    const entityItem = qs.numbers('entityItem', []);
    const approvalStatus = qs.numbers('approvalStatus', []);
    const start = qs.nullableDate('start', null);
    const end = qs.nullableDate('end', null);
    const categories = qs.numbers('category', []);
    const questionTitle = qs.strings('questionTitle', []);
    const response = qs.strings('response', []);

    const inspectionQueryParams = (): InspectionQuery => ({
      timezone: user.value!.timezone,
      page: page.value,
      perPage: perPage.value,
      search: search.value,
      assignee: assignee.value,
      entityItem: entityItem.value,
      status: [Status.Done, Status.SystemRejected],
      approvalStatus: approvalStatus.value,
      doneAtStart: start.value,
      doneAtEnd: end.value,
      categories: categories.value,
      sort: sort.value,
      dir: dir.value,
      overdue: null,
      questionTitle: questionTitle.value,
      response: response.value,
      doneBy: doneBy.value,
      lazyPagination: 1,
    });

    // get filters task
    const approvalStatuses = ref<SelectOptions>([]);
    const users = ref<FilterEntry[]>([]);
    const entityItems = ref<SelectOptions>([]);
    const getFilters = useTask(
      () => sequence(getAllUsers({ includeDeleted: true }), getAllEntityItems(), getAllApprovalStatuses()),
      ([userList, entityItemList, statusList]) => {
        users.value = userList.data;
        entityItems.value = filterListToOptions(entityItemList);
        approvalStatuses.value = filterListToOptions(statusList).map(({ value, label }) => ({
          value,
          label: approvalStatusToText(label as ApprovalStatus),
        }));
      },
    );

    const activeInspectionManageIssues = ref<null | InspectionRecord>(null);

    // get list task
    const list = ref<InspectionList>(emptyList());
    const getList = useTask(
      () => getInspectionList(inspectionQueryParams()),
      listResponse => {
        list.value = listResponse;

        // if the modal to create an issue is opened
        // we need to get the inspection with the newest
        // issues and populate `activeInspectionManageIssues`.
        // This is used in `manageIssueModal` component.
        if (activeInspectionManageIssues.value) {
          activeInspectionManageIssues.value = (
            list.value.data.find(i => i.id === activeInspectionManageIssues.value!.id)!
          );
        }

        if (total.value === 0) {
          total.value = listResponse.meta.total || listResponse.data.length;

          if (listResponse.data.length < perPage.value && page.value === 1) {
            total.value = listResponse.data.length;
            isPaginationFetchRequired.value = false;
          }
        }

        if (isPaginationFetchRequired.value) {
          totalLoading.value = true;
          isPaginationFetchRequired.value = false;
          getListPagination.run();
        }
      },
    );

    const getListPagination = useTask(
      () => getInspectionPagination(inspectionQueryParams()),
      paginationResponse => {
        total.value = paginationResponse.data.total;
        totalLoading.value = false;
      },
    );

    watch([
      page,
      perPage,
      search,
      assignee,
      entityItem,
      response,
      questionTitle,
      approvalStatus,
      start,
      end,
      doneBy,
      sort,
      dir,
    ], getList.run);

    watch([
      search,
      assignee,
      entityItem,
      response,
      questionTitle,
      approvalStatus,
      start,
      end,
      doneBy,
    ], () => {
      isPaginationFetchRequired.value = true;
    });

    const redirectRoute = ({ row }: { row: InspectionRecord }): RouteName =>
      row.data.status === Status.Pending ? 'inspectionEdit' : 'inspectionDetail';

    const { getConductedAt } = useSubmittedForm();

    const {
      selectedResources,
      deleteBulkTask,
      onCheckedRowsUpdate,
      isBulkDeletionDisabled,
    } = useBulkDeletable<InspectionRecord>({
      getList,
      bulkDeleteFn: bulkDelete,
      mapIds: record => record.data.inspection.id,
      // eslint-disable-next-line max-len
      onDeleteFilterFn: inspection => (currentElement => currentElement.data.inspection.id !== inspection.data.inspection.id),
    });

    const isManageIssueModalOpen = ref(false);

    const closeModal = () => {
      isManageIssueModalOpen.value = false;
      activeInspectionManageIssues.value = null;
    };

    const onManageIssueButtonClick = (inspection: InspectionRecord) => {
      activeInspectionManageIssues.value = inspection;
      isManageIssueModalOpen.value = true;
    };

    const mergeAttributesIssueCreation = computed(() => {
      if (activeInspectionManageIssues.value === null) {
        return {};
      }

      return {
        inspection: activeInspectionManageIssues.value.data.inspection.id,
        type: IssueType.ManualForm,
      };
    });

    const onIssueCreated = () => getList.run();
    const onIssueUpdated = () => getList.run();
    const filterAutomaticFormAndManualFormIssues = (issue: IssueRecord) => (
      issue.data.type === IssueType.AutomaticForm || issue.data.type === IssueType.ManualForm
    );
    const issues = computed(() => activeInspectionManageIssues.value
      ? activeInspectionManageIssues
        .value
        .data
        .issues
        .filter(filterAutomaticFormAndManualFormIssues)
      : []);

    return function render() {
      return loader('div', performCheck, [
        isManageIssueModalOpen.value
          ? manageIssueModal({
            props: {
              active: isManageIssueModalOpen.value,
              mergeAttributes: mergeAttributesIssueCreation.value,
              issues: issues.value,
              issueListProps: {
                subtitle: t('inspections.issue-raised-submitted', {
                  numberIssuesRaised: issues.value.length.toString() ?? '0',
                }),
              },
            },
            on: {
              close: closeModal,
              'issue:created': onIssueCreated,
              'issue:updated': onIssueUpdated,
            },
          }) : null,

        checkResult.value ? indexView(
          [
            filterFormLoader(getFilters, [
              bulkDeleteButton({ resources: selectedResources, disabled: isBulkDeletionDisabled }),
              searchFilter(search, resetPagination),
              questionTitleFilter(questionTitle, resetPagination),
              responseFilter(response, resetPagination),
              lastUpdatedFilter(start, end, resetPagination),
              h(ConductedByWithDeletedFilter, {
                props: {
                  value: doneBy,
                  users,
                  reset: resetPagination,
                },
              }),
              entityItemFilter(entityItem, entityItems.value, resetPagination),
              approvalsEnabledForOrganization.value
                && approvalStatusFilter(approvalStatus, approvalStatuses.value, resetPagination),
              inspectionIndexExport({
                props: {
                  selectedResources: selectedResources.value,
                  formats: [SupportedExportFormatEnum.CSV, SupportedExportFormatEnum.XLSX],
                },
                on: {
                  exported: () => {
                    selectedResources.value = [];
                  },
                },
              }),
            ]),
          ],
          [
            box([
              loader('div', getList, [
                h(BulkDeletableTable, {
                  props: {
                    editRoute: redirectRoute,
                    entityIdPath: ['data', 'inspection', 'id'],
                    list: list.value.data,
                    deleteFn: remove,
                    sort: sort.value,
                    dir: dir.value,
                    loading: deleteBulkTask.state.value.pending,
                    selectedRecords: selectedResources.value,
                    paginationLinks: list.value.meta,
                    isRowCheckable: () => true,
                    recordName: {
                      plural: t('common.inspections'),
                      singular: t('common.submitted') + t('common.inspection'),
                    },
                  },
                  on: {
                    'update:checkedRows': onCheckedRowsUpdate,
                    sort: (field: string, direction: SortDirection) => qs.updateMultiple(() => {
                      onSortChange(field, direction);
                      resetPagination();
                    }),
                    deleted: (id: number) => eventBus.$emit(Events.inspectionDeleted, { id }),
                  },
                  scopedSlots: {
                    default: () => [
                      textColumn(
                        {
                          field: 'doneAt',
                          label: t('checklist.conducted-at'),
                          sortable: true,
                        }, {
                          default: ({ row: inspection }: { row: InspectionRecord }) => [getConductedAt(inspection)],
                        },
                      ),
                      approvalsEnabledForOrganization.value
                        ? column(
                          {
                            field: 'title',
                            label: t('common.title'),
                            sortable: true,
                            cellClass: 'table__title-cell inspections-submitted__table-title',
                            headerClass: 'inspections-submitted__table-title-header',
                          }, {
                            default: ({ row: inspection }: { row: InspectionRecord }) => {
                              const title = inspection.data.inspectionTitle || inspection.data.title;
                              return h('span', { attrs: { title } }, title);
                            },
                          },
                        )
                        : column(
                          {
                            field: 'title',
                            label: t('common.title'),
                            sortable: true,
                            cellClass: 'table__title-cell',
                          }, {
                            default: ({ row: inspection }: { row: InspectionRecord }) => [
                              inspection.data.inspectionTitle || inspection.data.title,
                            ],
                          },
                        ),
                      textColumn({
                        field: 'completedBy',
                        label: t('checklist.conducted-by'),
                        sortable: true,
                      }, {
                        default: ({ row: inspection }: { row: InspectionRecord }) =>
                          [inspection.data.doneBy ? inspection.data.doneBy.data.name : null],
                      }),
                      textColumn(
                        {
                          field: 'entityItem',
                          label: t('common.entity-item'),
                          sortable: true,
                        }, {
                          default: ({ row: inspection }: { row: InspectionRecord }) => inspection.data.entityItem
                            ? inspection.data.entityItem.data.title
                            : null,
                        },
                      ),
                      textColumn(
                        {
                          field: 'dueAt',
                          label: t('common.deadline'),
                          cellClass: 'deadline-column',
                          sortable: true,
                        }, {
                          default: ({ row: inspection }: { row: InspectionRecord }) => inspection.data.dueAt
                            ? [h('span', d(inspection.data.dueAt))]
                            : [tag('neutral', t('common.no-deadline'))],
                        },
                      ),
                      textColumn(
                        {
                          field: 'inspectionResult',
                          label: t('common.percentage'),
                          sortable: true,
                        }, {
                          default: ({ row: inspection }: { row: InspectionRecord }) =>
                            inspection.data.inspection.percentage
                              ? `${toFixed(inspection.data.inspection.percentage * 100)}%`
                              : null,
                          header: () => h('div', { class: 'is-relative', slot: 'header' }, [
                            h('span', t('common.percentage')),
                            h(Tooltip, {
                              props: {
                                label: t('feature-explanation.percentage-done'),
                                multilined: true,
                                position: 'is-bottom',
                              },
                            }, [
                              h('div', [
                                featureExplanationIcon(),
                              ]),
                            ]),
                            h(SortingIcon, {
                              props: {
                                field: 'inspectionResult',
                                sort: sort.value,
                                direction: dir.value,
                              },
                            }),
                          ]),
                        },
                      ),
                      approvalsEnabledForOrganization.value
                        ? textColumn(
                          {
                            field: 'approvalStatus',
                            label: t('common.approval-status'),
                            cellClass: 'approval-column',
                            sortable: false,
                          }, {
                            default: ({ row: inspection }: { row: InspectionRecord }) =>
                              inspection.data.inspection.approval && inspection.data.inspection.approval.status
                                ? [tag(
                                  approvalStatusTagClass(inspection.data.inspection.approval!.status),
                                  approvalStatusToText(inspection.data.inspection.approval!.status),
                                )]
                                : [tag('neutral', '-')],
                          },
                        )
                        : null,
                    ],
                    beforeDeleteButton: ({ row: inspection }: { row: InspectionRecord }) => {
                      if (inspection.data.status === Status.SystemRejected) {
                        return h(SystemRejectedButton, {
                          props: {
                            cssClass: 'mr-2',
                            reason: inspection.data.inspection.system.reasonCode,
                          },
                        });
                      }
                      return manageIssuesButton({
                        class: 'mr-2',
                        props: {
                          issues: inspection.data.issues.filter(filterAutomaticFormAndManualFormIssues),
                        },
                        on: { click: () => onManageIssueButtonClick(inspection) },
                      });
                    },
                  },
                }),
                h(Pagination, {
                  props: {
                    current: page.value,
                    total: total.value,
                    perPage: perPage.value,
                    totalLoading: totalLoading.value,
                  },
                }),
              ]),
            ]),
          ],
        ) : box([h(InspectionDoneEmpty)]),
      ], false);
    };
  },
});
